import PageRendererHelperBase from './PageRendererHelperBase';
import PicabooConstants from '../../../../common/PicabooConstants';
import { GraphicItemUtils } from '../../../../utils/GraphicItemUtils';
import { GraphicItemMasks } from './GraphicItemMasks';
import { fabric } from 'fabric';
import logEvent from '../../../../utils/TagManager';

export default class PageRendererGraphicItemHelper extends PageRendererHelperBase {
  constructor(
    dim,
    canvas,
    images,
    imagePath,
    ratioWidth,
    resolutionFactor,
    interactive,
    nodeRender,
    isMobile,
  ) {
    super(dim, canvas, ratioWidth, interactive, nodeRender, isMobile);
    this._images = images;
    this._imagePath = imagePath;
    this.resolutionFactor = resolutionFactor;
    this.isLowRes = false;
  }

  areDimensionsUnchanged(pi, existingItem) {
    return (
      super.areDimensionsUnchanged(pi, existingItem) &&
      GraphicItemUtils.shouldApplyFilter(pi, 'Mirror') ===
        GraphicItemUtils.shouldApplyFilter(existingItem.pageItemVO, 'Mirror') &&
      GraphicItemUtils.shouldApplyFilter(pi, 'VerticalMirror') ===
        GraphicItemUtils.shouldApplyFilter(
          existingItem.pageItemVO,
          'VerticalMirror',
        )
    );
  }

  setLowRes(pi) {
    if (
      pi.get('imageID') &&
      this.projectImage &&
      !this.projectImage.serverImage
    ) {
      let rotated =
        pi.get('image_rotation') == 90 || pi.get('image_rotation') == 270;
      let piDim = {
        x: pi.get('image_positionX'),
        y: pi.get('image_positionY'),
        width: rotated ? pi.get('image_height') : pi.get('image_width'),
        height: rotated ? pi.get('image_width') : pi.get('image_height'),
      };
      this.isLowRes =
        this._interactive &&
        GraphicItemUtils.isLowRes(
          piDim,
          this.projectImage,
          this.resolutionFactor,
        );
    }
  }

  drawBackgroundImage(pi) {
    return new Promise((resolve) => {
      this.projectImage = this.getProjectImage(pi.get('imageID'));

      if (!this.projectImage) {
        resolve({ fail: 'Missing Image' });
        return;
      }

      this.setLowRes();
      this.wellWidth = this._canvas.width;
      this.wellHeight = this._canvas.height;
      this.x = 0;
      this.y = 0;
      this.imageWidth = pi.get('image_width') * this.ratioWidth;
      this.imageHeight = pi.get('image_height') * this.ratioWidth;

      let existingDimensions =
        GraphicItemUtils.backgroundDimensionCache[
          pi.get('pageID') + '_' + pi.get('id')
        ];
      if (existingDimensions) {
        if (
          existingDimensions.wellWidth != this.wellWidth ||
          existingDimensions.wellHeight != this.wellHeight ||
          existingDimensions.imageWidth != this.imageWidth ||
          existingDimensions.imageHeight != this.imageHeight
        ) {
          delete GraphicItemUtils.imageCache[
            pi.get('pageID') + '_' + pi.get('id')
          ];
          delete GraphicItemUtils.imageCache[pi.get('imageID')];
        }
      }
      GraphicItemUtils.backgroundDimensionCache[
        pi.get('pageID') + '_' + pi.get('id')
      ] = {
        wellWidth: this.wellWidth,
        wellHeight: this.wellHeight,
        imageWidth: this.imageWidth,
        imageHeight: this.imageHeight,
      };

      this.loadImages(pi).then((response) => {
        this.loadedImages = response.images;
        pi = response.pi;
        const img = this.loadedImages.image;

        if (!global.isRenderer && !this.nodeRender)
          GraphicItemUtils.imageCache[pi.get('imageID')] = img;
        if (this.nodeRender && this.loadedImages.image !== img) {
          this.loadedImages.image.src = null;
          this.loadedImages.image = null;
        }

        this.getExifData(pi.get('imageID'), img).then((image_orientation) => {
          this.loadedImages.image = this.resizeImage(
            pi,
            img,
            image_orientation,
          );
          this.createFabricGroup(pi, this.isLowRes).then((group) => {
            resolve({ image: group, processedImage: img });
          });
        });
      });
    });
  }

  drawGraphicItem(pi, wellWidth, wellHeight, x, y) {
    return new Promise((resolve, reject) => {
      /**
       * A graphic item represents a group that contains a rectangle (for border) and an image and an optional corner overlay.
       */

      wellWidth = Math.max(wellWidth, 1);
      wellHeight = Math.max(wellHeight, 1);

      this.projectImage = this.getProjectImage(pi.get('imageID'));

      //STICKERS suck, and sometimes have different casing on the IDs.
      if (!this.projectImage) {
        this.projectImage = this.getProjectImage(
          pi.get('imageID').toLowerCase(),
        );
      }
      if (!this.projectImage) {
        this.projectImage = this.getProjectImage(
          pi.get('imageID').toUpperCase(),
        );
      }

      this.imageWidth = pi.get('image_width') * this.ratioWidth;
      this.imageHeight = pi.get('image_height') * this.ratioWidth;
      this.wellWidth = wellWidth;
      this.wellHeight = wellHeight;
      this.x = x;
      this.y = y;

      this.populateFilters(pi);
      this.setLowRes(pi);

      let existingItem = this.getExistingCanvasObject(pi);

      if (
        pi.get('imageID') &&
        existingItem &&
        (!existingItem.pageItemVO.get('imageID') ||
          pi.get('imageID') !== existingItem.pageItemVO.get('imageID'))
      ) {
        //this was an empty well, so we need to remove the placeholder text
        this._canvas.remove(existingItem);
        existingItem = undefined;
      }

      if (existingItem != undefined) {
        if (this.isObjectUnchanged(pi, existingItem)) {
          resolve({ group: existingItem });
          return;
        }
        if (
          !this.isShapeUnchanged(pi, existingItem) ||
          !this.areDimensionsUnchanged(pi, existingItem) ||
          !this.isBorderUnchanged(pi, existingItem)
        ) {
          let groupItems = this.decompileGroup(existingItem);

          if (!groupItems) {
            reject('GROUPING_ERROR');
            return;
          }

          this.loadImages(pi, true).then((response) => {
            this.loadedImages = response.images;
            pi = response.pi;
            this.processImage(pi, existingItem).then((processedImage) => {
              existingItem.remove(groupItems.img);
              existingItem.remove(groupItems.border);
              if (groupItems.controls) {
                groupItems.controls.forEach((i) => existingItem.remove(i));
              }

              let newImage = this.getFabricImage(pi);
              let newBorder = this.getBorderRect(pi);

              existingItem.add(newBorder);
              existingItem.add(newImage);

              if (groupItems.controls) {
                groupItems.controls.forEach((i) => existingItem.add(i));
              }

              this.updateObjectProperties(existingItem, pi, this.isLowRes).then(
                (group) => {
                  resolve({ group: group, processedImage: processedImage });
                },
              );
            });
          });

          return;
        }

        this.updateObjectProperties(existingItem, pi).then((group) => {
          resolve({ group: group });
        });
      } else {
        this.addNewObject(pi, this.isLowRes)
          .then((ret) => {
            if (
              Math.floor(wellWidth) !== Math.floor(wellHeight) &&
              Math.floor(wellWidth) === ret.processedImage.height &&
              Math.floor(wellHeight) === ret.processedImage.width
            ) {
              let img = this.getProjectImage(pi.get('imageID'));
              if (
                img.get('orientation') ==
                  PicabooConstants.IMAGE_ORIENTATION.LEFT ||
                img.get('orientation') ==
                  PicabooConstants.IMAGE_ORIENTATION.RIGHT
              ) {
                resolve({
                  group: ret.group,
                  processedImage: ret.processedImage,
                });
              } else {
                reject('ROTATION_ERROR');
              }
            } else {
              resolve({ group: ret.group, processedImage: ret.processedImage });
            }
          })
          .catch((err) => {
            if (this._interactive) {
              logEvent('Image', 'Load Error', 'Showing clear cache warning', 0);
              resolve({ group: this.getDamagedWell(pi), processedImage: null });
            } else {
              reject(err);
            }
          });
      }
    });
  }

  loadImages(pi, loadWellImage = true) {
    return new Promise((resolve, reject) => {
      let imagesToLoad = [];
      const cacheImageID = pi.get('imageID');

      let images = {
        image: undefined,
      };

      if (loadWellImage) {
        if (GraphicItemUtils.imageCache[cacheImageID] != undefined) {
          images.image = GraphicItemUtils.imageCache[cacheImageID];
        } else {
          let { url, fallbackUrl } = GraphicItemUtils.getURLForDimensions(
            this.projectImage,
            this.imageWidth,
            this.imageHeight,
            this._imagePath,
          );

          function tryParseDimension(fn) {
            try {
              return parseInt(fn());
            } catch (e) {
              console.error('Failed to parse project image width/height', e);
              return -1;
            }
          }

          imagesToLoad.push({
            type: 'img',
            id: cacheImageID,
            url,
            fallbackUrl,
            img: null,
            imageType: this.projectImage.get('imageType'),
            originalImageWidth: tryParseDimension(() =>
              this.projectImage.get('width'),
            ),
            originalImageHeight: tryParseDimension(() =>
              this.projectImage.get('height'),
            ),
          });
        }
      }

      if (imagesToLoad.length === 0) {
        resolve({ images, pi });
        return;
      }

      GraphicItemUtils.loadImages(
        imagesToLoad,
        [],
        function (loadedImages, err) {
          if (err) {
            reject(err);
          } else {
            loadedImages.forEach((image) => {
              switch (image.type) {
                case 'img':
                  images.image = image.img;
                  images.fromCache = image.fromCache;
                  break;
              }
            });
            resolve({ images, pi });
          }
        },
      );
    });
  }

  imageExists(url) {
    if (!url) {
      return false;
    }

    try {
      var https = new XMLHttpRequest();
      https.open('GET', url, false);
      https.setRequestHeader('Range', 'bytes=0-0');
      https.send();

      return https.status != 404 && https.status != 403;
    } catch (e) {
      return false;
    }
  }

  resizeImage(pi, loadedImage, image_orientation = 1) {
    let imageWidth = pi.get('image_width') * this.ratioWidth;
    let imageHeight = pi.get('image_height') * this.ratioWidth;
    let imageXOffset = pi.get('image_positionX') * this.ratioWidth;
    let imageYOffset = pi.get('image_positionY') * this.ratioWidth;
    this.setLowRes(pi);

    let mirrorHorizontal = GraphicItemUtils.shouldApplyFilter(pi, 'Mirror');
    let mirrorVertical = GraphicItemUtils.shouldApplyFilter(
      pi,
      'VerticalMirror',
    );

    let resizedImage = GraphicItemUtils.resizeImage(
      loadedImage,
      imageWidth * window.devicePixelRatio,
      imageHeight * window.devicePixelRatio,
      imageXOffset * window.devicePixelRatio,
      imageYOffset * window.devicePixelRatio,
      this.wellWidth * window.devicePixelRatio,
      this.wellHeight * window.devicePixelRatio,
      this.projectImage,
      pi.get('image_rotation'),
      mirrorHorizontal,
      mirrorVertical,
      image_orientation,
    );
    if (this.nodeRender) {
      loadedImage.src = null;
      loadedImage = null;
    }
    return resizedImage;
  }

  getFabricImage(pi) {
    let img = new fabric.Image();
    return this.populateFabricImage(img, pi);
  }

  populateFabricImage(img, pi) {
    let imageOpacity = pi.get('alpha') || 1;
    img.lockUniScaling = false;

    let fit = GraphicItemUtils.fitImage(
      img.width,
      img.height,
      this.wellWidth,
      this.wellHeight,
      this.projectImage,
    );
    img.width = fit.width;
    img.height = fit.height;
    img.originX = 'center';
    img.originY = 'center';
    img.picabooType = 'userImage';
    img.opacity = imageOpacity;

    img.needsItsOwnCache = () => {
      return true;
    };
    img.setElement(this.loadedImages.image);
    img.pageItemVO = pi;

    this.correctOrientation(img, pi);

    let maskShape = pi.get('maskShape') == 'null' ? null : pi.get('maskShape');

    if (maskShape) {
      img.clipTo = (ctx) => {
        GraphicItemMasks.applyMask(
          ctx,
          maskShape,
          this.wellWidth,
          this.wellHeight,
        );
      };
    } else {
      img.clipTo = null;
    }

    return img;
  }

  correctOrientation(img, pi) {
    //We have two "rotation" values to deal with:
    //1. 'orientation' - from the exif data - we apply this to the image itself
    //2. 'rotation' - when the user rotates the image in the editor - we apply this to the GROUP

    if (this.projectImage) {
      let orientation = 0;
      // let swapDimensions = false;

      let imageOrientation =
        this.projectImage.get('orientation') || pi.get('image_orientation');
      switch (parseInt(imageOrientation)) {
        case PicabooConstants.IMAGE_ORIENTATION.LEFT:
          if (
            Number(this.projectImage.get('height')) <
            Number(this.projectImage.get('width'))
          ) {
            orientation = -90;
            // [img.width, img.height] = [img.height, img.width];
            // swapDimensions = true;
          }
          break;
        case PicabooConstants.IMAGE_ORIENTATION.RIGHT:
          if (
            Number(this.projectImage.get('height')) <
            Number(this.projectImage.get('width'))
          ) {
            orientation = 90;
            // [img.width, img.height] = [img.height, img.width];
            // swapDimensions = true;
          }
          break;
        case PicabooConstants.IMAGE_ORIENTATION.UPSIDE_DOWN:
          orientation = 180;
          break;
      }

      // img.swapDimensions = swapDimensions;
      img.rotate(orientation);
    }
  }

  getProjectImage(imageID) {
    if (!this._images) {
      return undefined;
    }

    return this._images.get(imageID);
  }

  getDamagedWell(pi) {
    const textSize = PageRendererGraphicItemHelper.getTextSize(
      this.wellWidth,
      'There was a problem loading this photo.',
    );

    let text = new fabric.Text(
      'There was a problem loading this photo.\nPlease try clearing your browser cache.',
      {
        left: this.wellWidth / 2,
        top: this.wellHeight / 2,
        width: this.wellWidth,
        height: this.wellHeight,
        originX: 'center',
        originY: 'center',
        fill: 'white',
        fontSize: textSize,
        fontFamily: 'Arial',
        textAlign: 'center',
      },
    );

    let shape = new fabric.Rect({
      left: this.wellWidth / 2,
      top: this.wellHeight / 2,
      width: this.wellWidth,
      height: this.wellHeight,
      originX: 'center',
      originY: 'center',
      fill: 'rgb(160,0,0)',
    });

    let groupOptions = {
      left: this.x,
      top: this.y,
      width: this.wellWidth,
      height: this.wellHeight,
      originX: 'left',
      originY: 'top',
      pageItemVO: pi,
    };

    let group = new fabric.Group([shape, text], groupOptions);

    return group;
  }
}
